// 通用 AI 流式请求客户端（NDJSON over fetch）
//
// 后端约定的事件类型：thinking / content / heartbeat / error / interrupted / done，
// 以及 AI 教练新增的 conversation / context / assistant_start / tool_call / tool_result / action。
// 与具体业务解耦：题面 AI 美化（problem/ai/enhancement）、提交 AI 解析（judge/ai/parse）
// 与 AI 教练（ai/chat）共用这一份解析逻辑。
//
// 未知事件类型一律忽略：后端先上线新事件时前端不会因此报错。

/** 一轮对话的 token 用量（done 事件携带，BYOK 用户关心成本） */
export interface AiStreamUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  /** 输入中命中厂商上下文缓存的部分（计费折扣）；未命中或厂商不返回时为 0 */
  cached_tokens: number;
}

/** 对话流中的动作卡片（服务端生成、前端只渲染，见设计文档 §6） */
export interface AiAction {
  type: string;
  label: string;
  payload?: Record<string, unknown>;
  disabled?: boolean;
  /** disabled 为 true 时的原因：可能是错误码数字，也可能是原文（前端按错误码查文案表，取不到再显示原文） */
  reason?: string | number;
}

/**
 * 上下文清单的一项：这次对话「带了什么 / 能查什么 / 看不到什么」。
 *
 * kind / type / label / note 全部由服务端下发，前端只按 kind 分组渲染、不自造文案——
 * 一旦前端自己写文案，「页面上写的」与「实际注入给模型的」迟早会分叉，
 * 而这份清单的全部价值就在一致。
 */
export interface AiContextItem {
  /** injected 已注入 / on_demand 可按需查询 / blocked 本次不可见 */
  kind: string;
  /** 数据来源类型（judge / problem / code / task / scene_ref），前端目前只按 kind 分组 */
  type: string;
  /** 一句话说明（chips 上的主文案） */
  label: string;
  /** 对象标识（如 #123 / didaoj1000），让用户核对「指的是哪一个」 */
  ref?: string;
  /** 补充说明（展开详情时展示） */
  note?: string;
}

/** 问答卡片的一个选项（服务端下发，前端只渲染） */
export interface AiAskOption {
  /** 选项文案 */
  label: string;
  /** 补充说明（如题目的 Key 与通过率） */
  description?: string;
  /**
   * 点选后发送给模型的内容：默认等于 label；
   * 选项带结构化标识（value = type:id）时由服务端翻译成「label（problem_id=3）」这类文本，
   * 让模型可以直接把它当参数用
   */
  reply: string;
  /** 结构化标识原文（type:id），仅用于排查与展示，前端不解析 */
  value?: string;
}

/** 问答卡片的一个问题 */
export interface AiAskQuestion {
  /** 不超过 12 字的短标签 */
  header?: string;
  /** 问题正文 */
  question: string;
  /** 是否多选（缺省为单选） */
  multi_select?: boolean;
  /** 2~4 个选项 */
  options: AiAskOption[];
}

/**
 * ask_user 动作的载荷：一组结构化问题 + 界面固定文案。
 *
 * 形态对齐 Claude Code 的 AskUserQuestion（1~4 题 × 2~4 选项），
 * 「其他（自行输入）」与「跳过」是界面常驻能力，不由模型生成。
 * 文案一律由服务端下发：前端不自造，改文案只改后端。
 */
export interface AiAskPayload {
  questions: AiAskQuestion[];
  other_label?: string;
  other_hint?: string;
  other_submit_label?: string;
  skip_label?: string;
  skip_reply?: string;
  multi_confirm_label?: string;
  multi_hint?: string;
}

export interface AiStreamEvent {
  type?: string;
  content?: string;
  message?: string;
  code?: number;
  /** done 事件可能携带的服务端回执（如 AI 解析的保存凭证） */
  receipt?: string;
  /** conversation：会话 id */
  conversation_id?: number;
  /** conversation：本轮用户消息落库后的 id */
  message_id?: number;
  /** assistant_start：本轮 LLM 调用的序号（1 起，工具往返会产生多条 assistant 消息） */
  round?: number;
  /** tool_call / tool_result：一次工具调用的标识 */
  call_id?: string;
  /** tool_call：工具 id */
  name?: string;
  /** tool_call：模型给出的参数（已解析；不保证结构正确） */
  args?: Record<string, unknown>;
  /** tool_result：是否成功（后端用指针类型，false 也会下发） */
  ok?: boolean;
  /** tool_result：结果摘要（不回传工具原始大文本） */
  summary?: string;
  /** action / done：动作卡片数组 */
  actions?: AiAction[];
  /** context：本轮上下文清单 */
  contexts?: AiContextItem[];
  /** done：本轮累计 token 用量 */
  usage?: AiStreamUsage;
}

export interface AiStreamCallbacks {
  onChunk: (chunk: string) => void;
  onThinking?: (chunk: string) => void;
  onHeartbeat?: () => void;
  /** code 为项目错误码（0 表示无具体错误码，此时应使用 message 原文） */
  onError?: (message: string, code?: number) => void;
  /** done 可能带载荷，因此回调收到整个事件（旧调用方忽略参数即可） */
  onDone?: (event: AiStreamEvent) => void;
  /** 流被中断：已产出部分内容但未正常结束，内容不可信 */
  onInterrupted?: (message?: string) => void;
  /** conversation：首帧返回会话与用户消息 id（新建会话时用于落位） */
  onConversation?: (e: { conversation_id: number; message_id: number }) => void;
  /**
   * assistant_start：每轮 LLM 调用开始。
   * 注意：该事件只带 round（本轮 assistant 消息要等本轮结束才落库），
   * 前端必须以 round 作为实时气泡的局部键，否则工具往返第二轮的 thinking/content 会追加进上一个气泡。
   */
  onAssistantStart?: (e: { round: number }) => void;
  /** tool_call：开始执行工具（前端显示「正在读取…」） */
  onToolCall?: (e: { call_id: string; name: string; args: Record<string, unknown> }) => void;
  /** tool_result：工具执行完成 */
  onToolResult?: (e: { call_id: string; ok: boolean; summary?: string }) => void;
  /** action：动作卡片（可能与 done.actions 重复，调用方需自行去重） */
  onAction?: (actions: AiAction[]) => void;
  /**
   * context：本轮上下文清单（紧跟首帧 conversation，在调用模型之前下发）。
   * 与 /ai/scene 的预览同源；服务端总是下发（空数组表示本场景不声明清单）
   */
  onContext?: (items: AiContextItem[]) => void;
}

/** 流式 AI 请求在建立阶段就失败（服务端返回普通 JSON 错误体或 HTTP 非 2xx） */
export class AiRequestError extends Error {
  readonly code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = "AiRequestError";
    this.code = code;
  }
}

/** 流异常中断（无终止事件）或协议无法解析 */
export class AiInterruptedError extends Error {
  constructor(message = "生成被中断，内容可能不完整") {
    super(message);
    this.name = "AiInterruptedError";
  }
}

/**
 * 探测流式接口是否返回了「普通 JSON 错误体」。
 *
 * 后端在写流式响应头之前的失败（参数错误 / 无权限 / 凭据未配置）走项目统一响应壳：
 * HTTP 200 + `{code, data}`。该响应体不是 NDJSON 事件流，若直接按行解析会一行都匹配不上，
 * 表现为前端既不报错也不出内容。这里提前识别并按错误抛出。
 *
 * 注意：必须用 clone() 读取，否则会消费掉原始响应体导致后续读流失效。
 */
async function throwIfPlainJsonError(response: Response) {
  try {
    const type = response.headers.get("content-type") ?? "";
    // 无 content-type 时无法判定，交由后续解析失败兜底
    if (type === "") {
      return;
    }
    // NDJSON 流式响应：正常路径
    if (type.includes("x-ndjson")) {
      return;
    }
    // 其余情况视为普通 JSON 错误体（200 + code）
    const body = await response.clone().json();
    const code = typeof body?.code === "number" ? body.code : 0;
    throw new AiRequestError(code, `AI 请求被拒绝（错误码 ${code}）`);
  } catch (e) {
    if (e instanceof AiRequestError) {
      throw e;
    }
    // 非 JSON 响应体：不在此处判定，交由后续流程兜底
  }
}

/**
 * 发起一次 NDJSON 流式 AI 请求并逐事件回调。
 *
 * 终止语义（调用方必须区分）：
 *   - 收到 done / error / interrupted 之一即正常返回（`sawTerminal` 记录）；
 *   - 未收到任何终止事件时抛 AiInterruptedError，避免把截断内容当成品。
 */
export async function streamAiNdjson(
  url: string,
  body: unknown,
  callbacks: AiStreamCallbacks,
  signal?: AbortSignal,
) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "include",
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new AiRequestError(0, `请求失败（HTTP ${response.status}）`);
  }

  await throwIfPlainJsonError(response);

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new AiInterruptedError("响应体不可读");
  }

  let buffer = "";

  // sawTerminal 记录是否收到过终止事件（done / error / interrupted）。
  // 该标志是区分「正常结束」与「流被掐断」的唯一依据——不能由函数是否返回推断。
  let sawTerminal = false;
  // 成功解析的事件行数：用于兜底「一行都没解析成功」的协议失配场景
  let parsedLines = 0;

  /** 按行解析并分发事件，返回 false 表示已收到终止事件 */
  const dispatchLine = (line: string): boolean => {
    if (!line.trim()) {
      return true;
    }
    let event: AiStreamEvent;
    try {
      event = JSON.parse(line);
    } catch {
      // 不再静默丢弃：保留原文便于排查协议问题
      console.warn("[AiStream] 无法解析的响应行：", line);
      return true;
    }
    parsedLines++;
    switch (event.type) {
      case "content":
        callbacks.onChunk(event.content ?? "");
        break;
      case "thinking":
        callbacks.onThinking?.(event.content ?? "");
        break;
      case "heartbeat":
        callbacks.onHeartbeat?.();
        break;
      case "conversation":
        callbacks.onConversation?.({
          conversation_id: event.conversation_id ?? 0,
          message_id: event.message_id ?? 0,
        });
        break;
      case "context":
        // 缺字段按空清单处理：服务端总是下发，缺失只可能出现在旧后端
        callbacks.onContext?.(event.contexts ?? []);
        break;
      case "assistant_start":
        callbacks.onAssistantStart?.({ round: event.round ?? 0 });
        break;
      case "tool_call":
        callbacks.onToolCall?.({
          call_id: event.call_id ?? "",
          name: event.name ?? "",
          args: event.args ?? {},
        });
        break;
      case "tool_result":
        // ok 用 ?? 兜底：后端虽会显式下发 false，但缺字段时按「失败」展示更安全
        callbacks.onToolResult?.({
          call_id: event.call_id ?? "",
          ok: event.ok ?? false,
          summary: event.summary,
        });
        break;
      case "action":
        callbacks.onAction?.(event.actions ?? []);
        break;
      case "error":
        sawTerminal = true;
        callbacks.onError?.(event.message ?? "AI 服务异常", event.code);
        return false;
      case "interrupted":
        sawTerminal = true;
        callbacks.onInterrupted?.(event.message);
        return false;
      case "done":
        sawTerminal = true;
        callbacks.onDone?.(event);
        return false;
      default:
        break;
    }
    return true;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!dispatchLine(line)) {
        return;
      }
    }
  }

  // 冲刷解码器与残留缓冲（服务端未以换行结尾时最后一行仍在 buffer 中）
  buffer += decoder.decode();
  if (buffer.trim()) {
    if (!dispatchLine(buffer)) {
      return;
    }
  }

  if (sawTerminal) {
    return;
  }

  // 未收到任何终止事件即走到这里，说明流被掐断或协议完全不匹配
  if (parsedLines === 0) {
    throw new AiInterruptedError("AI 服务返回了无法识别的内容，请稍后重试");
  }
  throw new AiInterruptedError();
}

/** AI 教练一轮对话的请求体（与后端 web/request/ai.go 的 AiChat 一致） */
export interface AiChatBody {
  /** 0 = 新建会话；>0 = 续接本人会话（此时服务端的 scene / ref 以库中记录为准） */
  conversation_id: number;
  scene: string;
  ref: { type: string; id: number };
  /** 打开教练时所在页面（路由 name，如 problem-detail）；仅新建会话时落库，供统计「用户从哪里问起」 */
  entry_page?: string;
  /** 打开教练时所在页面的真实访问路径（route.path + 查询参数名，如 /problem/P1001；查询取值不入库）；同样仅新建会话时落库 */
  entry_path?: string;
  message: string;
}

/**
 * AI 教练对话入口：POST /ai/chat（NDJSON 流式）。
 *
 * 建立阶段的失败（未登录 / 未配置 BYOK 10011 / 场景不存在 10095 / 会话不属于自己 10096）
 * 是普通 JSON 错误体，由 streamAiNdjson 识别并抛 AiRequestError（带 code），调用方按 code 查文案。
 */
export async function PostAiChat(body: AiChatBody, callbacks: AiStreamCallbacks, signal?: AbortSignal) {
  const baseURL = import.meta.env.VITE_API_BASE || "/api/";
  await streamAiNdjson(`${baseURL}ai/chat`, body, callbacks, signal);
}
